import { Platform, SettingKeys, TrialFeature } from '@invoice-simple/common';

import { getDocuments } from 'src/apis/docsAPI';
import { getExpense } from 'src/apis/expenseAPI';
import SettingListModel from 'src/models/SettingListModel';
import UserModel from 'src/models/UserModel';

export const isFreeTrialEnabled = () => {
  const user = UserModel.getInstance();

  const isFreeTrialActive =
    user.isSubActive && user.subscriptionOrderSku === 'com.invoicesimple.freetrial.14day';

  return isFreeTrialActive && !user.hadPaidSubsInThePast;
};

export const hasFreeTrialEnded = () => {
  if (isFreeTrialEnabled()) {
    return false;
  }

  const user = UserModel.getInstance();

  return !user.isSubActive && !user.hadPaidSubsInThePast && !!user.hadFreeTrialSubInThePast;
};

export const getSubscriptionActiveUntil = (): number => {
  const user = UserModel.getInstance();
  const freeTrialEnabled = isFreeTrialEnabled();
  const activeUntil = user.trialSub?.expiryTimestamp;

  if (!freeTrialEnabled || !activeUntil) {
    return 0;
  }

  return activeUntil;
};

export const trackFeatureCompletedEvent = async (user: UserModel): Promise<void> => {
  const completedFeaturesSet: Set<TrialFeature> = new Set();

  const settingList = user.settingList;
  const docs = await getDocuments(user.getParseAccount());
  const expense = await getExpense(user.getParseAccount());

  docs?.forEach((doc) => {
    if (doc.get('photos')) {
      completedFeaturesSet.add('add-photos');
    }

    if (doc.get('signDate')) {
      completedFeaturesSet.add('invoice-signature');
    }
  });

  if (hasBusinessInfo(settingList)) {
    completedFeaturesSet.add('business-info');
  }

  if (expense) {
    completedFeaturesSet.add('add-expense');
  }

  const completedFeatures = Array.from(completedFeaturesSet);

  if (completedFeatures.length > 0) {
    completedFeatures.forEach((feature) => {
      user.events.trackAction('free-trial-features-completed', {
        'completed-feature': feature,
        'completed-features': completedFeatures,
        'completed-features-total': completedFeatures.length,
        tour: 'organic-encounter',
        platform: Platform.WEB
      });
    });

    const trialFeatureSetting = settingList.getSetting(SettingKeys.TrialFeaturesExplored);

    const map = completedFeatures
      .map((feature) => toJSON(feature, 'completed'))
      .reduce((feature, status) => ({ ...feature, ...status }), {});

    trialFeatureSetting?.setValue(JSON.stringify(map));
  }
};

const toJSON = (key: string, value: string) => {
  return { [key]: value };
};

const hasBusinessInfo = (settingList: SettingListModel): boolean => {
  return (
    !!settingList.getSetting(SettingKeys.CompanyName)?.valStr ||
    !!settingList.getSetting(SettingKeys.CompanyBusinessNumber)?.valStr ||
    !!settingList.getSetting(SettingKeys.ContactPhone)?.valStr ||
    !!settingList.getSetting(SettingKeys.CompanyAddressLine1)?.valStr ||
    !!settingList.getSetting(SettingKeys.CompanyAddressLine2)?.valStr ||
    !!settingList.getSetting(SettingKeys.CompanyAddressLine3)?.valStr
  );
};
